import { Component, OnInit } from '@angular/core';
import {
  AdminService,
  SystemLogEvent,
  SystemLogHealthResponse,
  SyslogSeverity,
  SyslogComponentType,
  SystemLogsParams,
} from '../../core/services/admin.service';

@Component({
  selector: 'app-system-logs',
  templateUrl: './system-logs.component.html',
})
export class SystemLogsComponent implements OnInit {
  events: SystemLogEvent[] = [];
  loading = true;
  error = '';

  health: SystemLogHealthResponse | null = null;
  healthError = '';

  selectedEvent: SystemLogEvent | null = null;
  copied = false;

  filterDays: 1 | 3 | 7 = 1;
  filterSeverity: SyslogSeverity | '' = '';
  filterComponent: SyslogComponentType | '' = '';
  filterEventType = '';
  filterCorrelationId = '';

  readonly severities: SyslogSeverity[] = ['DEBUG', 'INFO', 'WARN', 'ERROR', 'CRITICAL'];
  readonly components: SyslogComponentType[] = [
    'DISPATCHER', 'WORKER', 'QUEUE', 'INTEGRATION',
    'RECONCILIATION', 'INFRA', 'SECURITY', 'API', 'SCHEDULER',
  ];

  constructor(private readonly adminService: AdminService) {}

  ngOnInit(): void {
    this.loadHealth();
    this.load();
  }

  get errorCount(): number {
    return this.events.filter(e => e.severity === 'ERROR' || e.severity === 'CRITICAL').length;
  }

  get warnCount(): number {
    return this.events.filter(e => e.severity === 'WARN').length;
  }

  applyFilters(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';

    const params: SystemLogsParams = { days: this.filterDays };
    if (this.filterSeverity)         params.severity      = this.filterSeverity;
    if (this.filterComponent)        params.component     = this.filterComponent;
    if (this.filterEventType.trim()) params.eventType     = this.filterEventType.trim();
    if (this.filterCorrelationId.trim()) params.correlationId = this.filterCorrelationId.trim();

    this.adminService.getSystemLogs(params).subscribe({
      next: res => {
        this.events  = res.events;
        this.loading = false;
      },
      error: () => {
        this.error   = 'Failed to load system logs.';
        this.loading = false;
      },
    });
  }

  private loadHealth(): void {
    this.adminService.getSystemLogHealth().subscribe({
      next:  h => { this.health = h; },
      error: () => { this.healthError = 'Health unavailable'; },
    });
  }

  openDrawer(event: SystemLogEvent): void {
    this.selectedEvent = event;
    this.copied = false;
  }

  closeDrawer(): void {
    this.selectedEvent = null;
    this.copied = false;
  }

  copyCorrelationId(id: string): void {
    navigator.clipboard.writeText(id).then(() => {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    });
  }

  metadataJson(meta: Record<string, unknown>): string {
    try { return JSON.stringify(meta, null, 2); } catch { return '{}'; }
  }

  metadataKeys(meta: Record<string, unknown>): string[] {
    return Object.keys(meta);
  }

  severityClass(severity: string): string {
    const map: Record<string, string> = {
      DEBUG:    'bg-slate-100 text-slate-500 border-slate-200',
      INFO:     'bg-sky-50 text-sky-700 border-sky-200',
      WARN:     'bg-amber-50 text-amber-700 border-amber-200',
      ERROR:    'bg-red-50 text-red-700 border-red-200',
      CRITICAL: 'bg-red-100 text-red-800 border-red-300',
    };
    return map[severity] ?? 'bg-slate-100 text-slate-500 border-slate-200';
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      SUCCESS:     'bg-emerald-50 text-emerald-700 border-emerald-200',
      FAILURE:     'bg-red-50 text-red-700 border-red-200',
      IN_PROGRESS: 'bg-violet-50 text-violet-700 border-violet-200',
      SKIPPED:     'bg-zinc-100 text-zinc-500 border-zinc-200',
    };
    return map[status] ?? 'bg-zinc-100 text-zinc-500 border-zinc-200';
  }

  componentClass(component: string): string {
    const map: Record<string, string> = {
      DISPATCHER: 'bg-violet-50 text-violet-700 border-violet-200',
      WORKER:     'bg-sky-50 text-sky-700 border-sky-200',
      QUEUE:      'bg-amber-50 text-amber-700 border-amber-200',
      INTEGRATION:'bg-teal-50 text-teal-700 border-teal-200',
      SECURITY:   'bg-red-50 text-red-700 border-red-200',
      INFRA:      'bg-zinc-100 text-zinc-600 border-zinc-200',
    };
    return map[component] ?? 'bg-zinc-100 text-zinc-600 border-zinc-200';
  }

  relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1)   return 'just now';
    if (m < 60)  return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)  return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }
}
